import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { SOCKET_URL } from '../config';
import { teams } from '../data/teams';

interface GameTeam {
  id: string;
  name: string;
  color: string;
  abbreviation?: string;
}

interface GameData {
  id: string;
  status: 'live' | 'completed' | 'scheduled';
  teams: {
    team1: GameTeam;
    team2: GameTeam;
  };
  score?: {
    team1: number;
    team2: number;
  };
}

interface LiveGame {
  id: string;
  team1: string;
  team2: string;
  status: 'live' | 'standby';
  anchorId?: string; // Anchor ID for scrolling (game_1, game_2, etc.)
}

const LiveGamesSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeGames, setActiveGames] = useState<(GameData | null)[]>([null, null]);

  // Function to get team abbreviation from team data
  const getTeamAbbreviation = (teamId: string): string => {
    const team = teams.find(t => t.id === teamId);
    if (team && team.abbreviation) {
      return team.abbreviation;
    }
    // Fallback to generating abbreviation if not found (2 letters)
    const teamName = team?.name || '';
    const words = teamName.split(' ');
    if (words.length > 1) {
      return words.map(word => word[0]?.toUpperCase() || '').join('').slice(0, 2);
    }
    return teamName.slice(0, 2).toUpperCase();
  };

  // Convert active games to live games format
  // Only show unique real games (no duplicates) - exclude future games for betting
  const liveGames = useMemo((): LiveGame[] => {
    const games: LiveGame[] = [];
    const seenGames = new Set<string>(); // Track unique team combinations
    
    // Helper to create a unique key for a game
    const getGameKey = (team1Id: string, team2Id: string): string => {
      // Always use the same order (smaller ID first) to avoid duplicates
      return team1Id < team2Id ? `${team1Id}-${team2Id}` : `${team2Id}-${team1Id}`;
    };
    
    // Only add active games (live or completed) - exclude nextGames (future betting games)
    activeGames.forEach((game, slotIndex) => {
      if (game && game.teams && game.teams.team1 && game.teams.team2) {
        const gameKey = getGameKey(game.teams.team1.id, game.teams.team2.id);
        if (!seenGames.has(gameKey)) {
          seenGames.add(gameKey);
          games.push({
            id: game.id || `active_${slotIndex}`,
            team1: getTeamAbbreviation(game.teams.team1.id),
            team2: getTeamAbbreviation(game.teams.team2.id),
            status: game.status === 'live' ? 'live' : 'standby',
            anchorId: `game_${slotIndex + 1}` // Map slot index to anchor ID (game_1, game_2)
          });
        }
      }
    });
    
    return games.slice(0, 5); // Limit to 5 games
  }, [activeGames]);

  // Determine overall status (Live if any game is live, otherwise Standby)
  const overallStatus = useMemo(() => {
    const hasLiveGame = activeGames.some(game => game && game.status === 'live');
    return hasLiveGame ? 'live' : 'standby';
  }, [activeGames]);

  // Socket connection for live game updates
  useEffect(() => {
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('Connected to game server from LiveGamesSidebar');
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from game server');
    });

    newSocket.on('continuous-game-start', (data: any) => {
      const { slot, game } = data;
      setActiveGames(prev => {
        const newGames = [...prev];
        newGames[slot] = game;
        return newGames;
      });
    });

    newSocket.on('continuous-game-update', (data: any) => {
      const { slot, game } = data;
      setActiveGames(prev => {
        const newGames = [...prev];
        if (newGames[slot] && newGames[slot]?.id === game.id) {
          newGames[slot] = {
            ...newGames[slot],
            ...game
          };
        } else {
          newGames[slot] = game;
        }
        return newGames;
      });
    });

    newSocket.on('continuous-game-complete', (data: any) => {
      const { slot } = data;
      setActiveGames(prev => {
        const newGames = [...prev];
        newGames[slot] = null;
        return newGames;
      });
    });

    newSocket.on('continuous-games-current', (data: any) => {
      setActiveGames(data.activeGames || [null, null]);
    });

    newSocket.on('continuous-next-games', (data: any) => {
      // Next games updated - no action needed as we only display active games
    });

    return () => {
      newSocket.close();
    };
  }, []);

  return (
    <div className="sidebar-wrapper">
      <div className="sidebar">
        <div className="sb--live-games">
          <div className="live-indicator">
            <div 
              style={{ 
                '--pulse-color': overallStatus === 'live' ? 'rgb(255,0,0)' : 'rgb(0,100,255)'
              } as React.CSSProperties & { '--pulse-color'?: string }}
              className="live-dot pulse-dot"
            ></div>
            <div className="live-label">{overallStatus === 'live' ? 'Live' : 'Standby'}</div>
          </div>
          <div className="live-game--list">
            {liveGames.length > 0 ? (
              liveGames.map((game, index) => {
                const handleClick = (e: React.MouseEvent) => {
                  e.preventDefault();
                  const targetPath = game.anchorId ? `/games#${game.anchorId}` : '/games';
                  if (location.pathname === '/games') {
                    // Already on games page, manually scroll with offset
                    if (game.anchorId) {
                      // Update hash without triggering default scroll behavior
                      window.history.replaceState(null, '', `/games#${game.anchorId}`);
                      
                      // Manually scroll with offset
                      setTimeout(() => {
                        const element = document.querySelector(`#${game.anchorId}`);
                        if (element) {
                          const elementPosition = element.getBoundingClientRect().top;
                          const offsetPosition = elementPosition + window.pageYOffset - 150;
                          window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                          });
                        }
                      }, 10);
                    }
                  } else {
                    // Navigate to games page with hash
                    navigate(targetPath);
                  }
                };
                return (
                  <a
                    key={game.id}
                    href={game.anchorId ? `/games#${game.anchorId}` : '/games'}
                    onClick={handleClick}
                    className="link--live-game game-screen w-inline-block"
                  >
                    <div className="ll-team">{game.team1}</div>
                    <div className="ll-divider">/</div>
                    <div className="ll-team">{game.team2}</div>
                  </a>
                );
              })
            ) : (
              <div className="link--live-game" style={{ opacity: 0.5 }}>
                <div className="ll-team">---</div>
                <div className="ll-divider">/</div>
                <div className="ll-team">---</div>
              </div>
            )}
          </div>
        </div>
        <button 
          type="button" 
          className="link-standard" 
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          onClick={(e) => {
            e.preventDefault();
            if (location.pathname === '/games') {
              // Already on games page, clear hash and scroll to top
              window.history.replaceState(null, '', '/games');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              // Navigate to games page (GamesPage will handle scrolling to top)
              navigate('/games');
            }
          }}
        >
          Upcoming /Betting
        </button>
      </div>
    </div>
  );
};

export default LiveGamesSidebar;

